import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { brandService } from '@/services/brandService';
import type { Brand, BrandQueryParams, CreateBrandParams, UpdateBrandParams } from '@/types/brand';

interface BrandState {
  items: Brand[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
}

const initialState: BrandState = {
  items: [],
  total: 0,
  page: 1,
  pageSize: 10,
  loading: false,
  error: null,
};

// 获取品牌列表
export const fetchBrands = createAsyncThunk(
  'brand/fetchBrands',
  async (params: BrandQueryParams) => {
    const response = await brandService.getBrandList(params);
    return response;
  }
);

// 创建品牌
export const createBrand = createAsyncThunk(
  'brand/createBrand',
  async (data: CreateBrandParams) => {
    const response = await brandService.createBrand(data);
    return response;
  }
);

// 更新品牌
export const updateBrand = createAsyncThunk(
  'brand/updateBrand',
  async ({ id, data }: { id: number; data: UpdateBrandParams }) => {
    const response = await brandService.updateBrand(data);
    return response;
  }
);

// 删除品牌
export const deleteBrand = createAsyncThunk(
  'brand/deleteBrand',
  async (id: number) => {
    await brandService.deleteBrand(id);
    return id;
  }
);

const brandSlice = createSlice({
  name: 'brand',
  initialState,
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 获取品牌列表
      .addCase(fetchBrands.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBrands.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
      })
      .addCase(fetchBrands.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取品牌列表失败';
      })
      // 创建品牌
      .addCase(createBrand.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBrand.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
        state.total += 1;
      })
      .addCase(createBrand.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '创建品牌失败';
      })
      // 更新品牌
      .addCase(updateBrand.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBrand.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateBrand.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '更新品牌失败';
      })
      // 删除品牌
      .addCase(deleteBrand.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBrand.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((item) => item.id !== action.payload);
        state.total -= 1;
      })
      .addCase(deleteBrand.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '删除品牌失败';
      });
  },
});

export const { setPage, setPageSize, clearError } = brandSlice.actions;
export default brandSlice.reducer; 